import {html} from 'lit-html';
import {repeat} from 'lit-html/directives/repeat';
import {unsafeHTML} from 'lit-html/directives/unsafe-html';
import {ifDefined} from 'lit-html/directives/if-defined';
import {styleMap} from 'lit-html/directives/style-map';

import TemplateRegistry from '../template/template-registry';
import {collapse, optionName, parseTextEditorProperty} from '../template/template-util.js';
import {optionsTemplate} from '../template/product-options.template.js';

import Util from '../util';
import Translate from '../translate';
import {cdnImg, cdnSize} from '../shopify-cdn';

import './group-editor.option.css';

const SVG_ICONS = {
    UPLOAD: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24" fill="currentColor"><path d="M15 4H5V20H19V8H15V4ZM3 2.9918C3 2.44405 3.44749 2 3.9985 2H16L20.9997 7L21 20.9925C21 21.5489 20.5551 22 20.0066 22H3.9934C3.44476 22 3 21.5447 3 21.0082V2.9918ZM13 12V16H11V12H8L12 8L16 12H13Z"></path></svg>',
    IMAGE: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24" fill="currentColor"><path  d="M21 15V18H24V20H21V23H19V20H16V18H19V15H21ZM21.0082 3C21.556 3 22 3.44495 22 3.9934V13H20V5H4V18.999L14 9L17 12V14.829L14 11.8284L6.827 19H14V21H2.9918C2.44405 21 2 20.5551 2 20.0066V3.9934C2 3.44476 2.45531 3 2.9918 3H21.0082ZM8 7C9.10457 7 10 7.89543 10 9C10 10.1046 9.10457 11 8 11C6.89543 11 6 10.1046 6 9C6 7.89543 6.89543 7 8 7Z"></path></svg>',
    TEXT: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24" fill="currentColor"><path d="M13 6v15h-2V6H5V4h14v2h-6z"/></svg>',
    COLOR: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C17.5222 2 22 5.97778 22 10.8889C22 13.9556 19.5111 16.4444 16.4444 16.4444H14.4778C13.5556 16.4444 12.8111 17.1889 12.8111 18.1111C12.8111 18.5333 12.9778 18.9222 13.2333 19.2111C13.5 19.5111 13.6667 19.9 13.6667 20.3333C13.6667 21.2556 12.9 22 12 22C6.47778 22 2 17.5222 2 12C2 6.47778 6.47778 2 12 2ZM10.8111 18.1111C10.8111 16.0843 12.451 14.4444 14.4778 14.4444H16.4444C18.4065 14.4444 20 12.851 20 10.8889C20 7.1392 16.4677 4 12 4C7.58235 4 4 7.58235 4 12C4 16.19 7.2226 19.6285 11.324 19.9718C10.9948 19.4168 10.8111 18.7761 10.8111 18.1111ZM7.5 12C6.67157 12 6 11.3284 6 10.5C6 9.67157 6.67157 9 7.5 9C8.32843 9 9 9.67157 9 10.5C9 11.3284 8.32843 12 7.5 12ZM16.5 12C15.6716 12 15 11.3284 15 10.5C15 9.67157 15.6716 9 16.5 9C17.3284 9 18 9.67157 18 10.5C18 11.3284 17.3284 12 16.5 12ZM12 9C11.1716 9 10.5 8.32843 10.5 7.5C10.5 6.67157 11.1716 6 12 6C12.8284 6 13.5 6.67157 13.5 7.5C13.5 8.32843 12.8284 9 12 9Z"></path></svg>',
    DEFAULT: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24" fill="currentColor"><path d="M2 18H12V20H2V18ZM2 11H22V13H2V11ZM2 4H22V6H2V4ZM18 18V15H20V18H23V20H20V23H18V20H15V18H18Z"></path></svg>',
}

const LINKED_COLOR_HIDDEN_CONDITION = {
    data: 'options',
    field: '__group_editor_linked_color_hidden__',
    operator: '=',
    value: '1'
};
const LINKED_COLOR_SUFFIX = '_color';

const isLinkedColorHidden = (conditions = []) => conditions[0]?.field === LINKED_COLOR_HIDDEN_CONDITION.field;

// Type metadata - icons and subtitles by type
const TYPE_ICONS = {
    'upload': SVG_ICONS.UPLOAD,
    'upload-lift': SVG_ICONS.UPLOAD,
    'text': SVG_ICONS.TEXT,
    'textarea': SVG_ICONS.TEXT,
    'library': SVG_ICONS.IMAGE,
    'img-swatch': SVG_ICONS.IMAGE
};

class GroupEditorController {

    constructor(onChange, getOption) {
        this.onChange = onChange;
        this.getOption = getOption;
        this.config = null; // reference updated on each render
        this.option = null; // reference updated on each render

        // Persistent state (survives option object replacement in editor mode)
        this._instances = [];        // Stores option instances
        this._initialized = false;   // One-time init flag on CONTROLLER (not option)

        // Template storage (initialized once in initTemplates)
        this.templates = {};
        this.names = [];
        this.buttons = [];
        this.templatesPreview = {};
        this.layers = {};

        // Persistent layer instance storage (survives previewConfig replacement in editor mode)
        this._layerInstances = {};  // { templateName: [layer1, layer2, ...] }

        // Variant parents: layers with same params.option but different conditions/filters
        // { templateName: [{ parent: layer, template: layerTemplate }] }
        this._variantParents = {};
        this._hasVariants = false;  // Fast guard: skip _syncVariantChildren when no variants exist

        // Collapse state for card UI (survives re-renders)
        this._collapseState = {};  // { templateName: boolean } - true = open

        // Multi-select state: { templateName: { option, _layers, parentLayer } }
        this._multiSelectTemplates = {};

        this._openLinkedColorInstanceName = null;
    }

    /**
     * One-time template extraction (called only when controller created)
     * Extracts template definitions from option.options and clears the array
     */
    initTemplates() {
        if (this.option.options) {
            for (let i = 0; i < this.option.options.length; i++) {
                let optionTemplate = Util.deepCopy(this.option.options[i], ['_*']);
                this._processTemplate(optionTemplate);
            }
            // clear templates from option.options (they're now in this.templates)
            this.option.options.length = 0;
        }
        this._initialized = true;
    }

    /**
     * Re-extract templates from option.options (editor mode config update)
     * Called when server resends templates with potentially new/changed types
     */
    _reinitTemplates() {
        this.templates = {};
        this.names = [];
        this.buttons = [];
        this._multiSelectTemplates = {};  // Reset multi-select state for fresh detection

        for (const optionTemplate of this.option.options) {
            const template = Util.deepCopy(optionTemplate, ['_*']); // primarily exclude controllers
            this._processTemplate(template);
        }
        // Note: templatesPreview and layers will be rebuilt in syncPreviewLayers()
    }

    /**
     * Process a single template - shared logic between init and reinit
     * Handles storage, button creation, and multi-select detection
     */
    _processTemplate(optionTemplate) {
        // Store template by name (unique identifier)
        this.templates[optionTemplate.name] = optionTemplate;

        // Track name (used to detect templates vs instances)
        this.names.push(optionTemplate.name);

        // Add button
        this.buttons.push({
            name: optionTemplate.name,
            type: optionTemplate.type,
            label: optionTemplate.params?.groupEditor?.button
                   || 'Add ' + optionTemplate.label
        });

        // Multi-select detection: params flag OR auto-enable for swatch types
        if (optionTemplate.params?.multiSelect === true ||
            optionTemplate.type.includes('swatch') ||
            optionTemplate.type === 'library' ||
            optionTemplate.type === 'upload-lift') {
            optionTemplate._isMultiSelect = true;
            this._multiSelectTemplates[optionTemplate.name] = {
                option: optionTemplate,
                // Flag: template handles its own value storage and UI display (e.g., upload-lift)
                hasCustomUI: optionTemplate.type === 'upload-lift'
            };
        }
    }

    /**
     * Sync controller instances to option.options (handles updateOptions case)
     * Detects whether option.options contains templates or instances and handles both cases
     */
    syncInstances() {
        // Detect if option.options contains TEMPLATES (server resent config)
        // Templates have names matching this.names (the original template names)
        const hasTemplates = this.option.options?.length > 0 &&
            this.option.options.some(o => this.names.includes(o.name));

        if (hasTemplates) {
            // Server sent templates again (editor mode config update)
            // Re-extract templates in case they changed
            this._reinitTemplates();

            // Restore instances from controller, filtering by valid template names
            // AND merge new template params into existing instances
            this.option.options.length = 0;
            const restoredInstanceNames = new Set();
            for (const instance of this._instances) {
                const templateName = instance._templateName;
                if (templateName && this.templates[templateName]) {
                    // Merge template params into instance (new settings from template override)
                    const templateParams = this.templates[templateName].params || {};
                    instance.params = Object.assign({}, instance.params, templateParams);
                    this.option.options.push(instance);
                    restoredInstanceNames.add(instance.name);
                }
            }

            for (const instance of this._instances) {
                if (instance._isLinkedColorOption && restoredInstanceNames.has(instance._parentInstanceName)) {
                    this.option.options.push(instance);
                }
            }

            for (const instance of this.option.options) {
                if (instance._templateName && instance._isLinkedColorOption !== true) {
                    const sourceValue = this._getLinkedColorSource(instance)?.option?.value;
                    this._ensureLinkedColorOption(instance, sourceValue);
                }
            }

            this._sortOptions();
            return;
        }

        // If option.options has instances (not templates), capture them
        if (this.option.options?.length > 0) {
            // These are actual instances, sync to controller
            this._instances = this.option.options;
            return;
        }

        // If option.options is empty but controller has instances, restore them
        if (this._instances.length > 0) {
            this.option.options = this.option.options || [];
            for (const instance of this._instances) {
                const templateName = instance._templateName;
                if (templateName && this.templates[templateName]) {
                    this.option.options.push(instance);
                }
            }
            for (const instance of this._instances) {
                if (instance._isLinkedColorOption && this._getInstanceByName(instance._parentInstanceName)) {
                    this.option.options.push(instance);
                }
            }
            this._sortOptions();
        }
    }

    /**
     * Sync preview layers (handles updatePreview case)
     * Restores layer instances from cache when editor mode wipes layer.layers
     */
    syncPreviewLayers() {
        if (!this.config.previewConfig) return;

        let restored = false;

        // Build list of all layers: main + views
        let allLayers = [...this.config.previewConfig.layers];
        if (this.config.previewConfig.gallery && this.config.previewConfig.views?.length) {
            for (const view of this.config.previewConfig.views) {
                allLayers.push(...view.layers);
            }
        }

        // Reset layer references (rebuilt from fresh previewConfig each sync)
        this._hasVariants = false;
        for (const name of this.names) {
            delete this.layers[name];
            this._variantParents[name] = [];
        }

        for (const layer of allLayers) {
            // Use layer.params.option as the template name (unique identifier)
            const templateName = layer.params.option;
            if (templateName && this.names.includes(templateName)) {

                // Multiple parent layers can share the same params.option (e.g. normal + grayscale variant
                // with different conditions/filters). First becomes primary, others are variants.
                // Variant children are synced from primary via _syncVariantChildren().
                if (this.layers[templateName]) {
                    if (!layer.layers) layer.layers = [];
                    const variantTemplate = Util.deepCopy(layer, ['layers', '_*']);
                    variantTemplate.conditions.length = 0;
                    this._variantParents[templateName].push({ parent: layer, template: variantTemplate });
                    this._hasVariants = true;
                    continue;
                }

                // Update parent layer reference (may be new object from editor)
                this.layers[templateName] = layer;
                if (!layer.layers) layer.layers = [];

                // Extract template (re-extract in editor mode when server updates layer params)
                if (!this.templatesPreview[templateName] || this.config.editorMode) {
                    this.templatesPreview[templateName] = Util.deepCopy(layer, ['layers', '_*']);
                    this.templatesPreview[templateName].conditions.length = 0;

                    // EDGE case: overlay_values layer type - pre-created child layers from backend
                    if (layer.type === 'overlay_values' && !layer._cleared) { // IMPORTANT: cleared flag to only run again when server updates layers in editorMode
                        // Initialize or update multi-select data structure
                        if (!this._multiSelectTemplates[templateName]) {
                            this._multiSelectTemplates[templateName] = {
                                option: this.templates[templateName],  // The swatch template
                                _layers: Util.deepCopy(layer.layers),  // Pre-created child layers
                                parentLayer: layer                      // Reference to parent layer
                            };
                        } else {
                            // Editor mode: update _layers and parentLayer reference
                            this._multiSelectTemplates[templateName]._layers = Util.deepCopy(layer.layers);
                            this._multiSelectTemplates[templateName].parentLayer = layer;
                        }

                        // Mark template for UI branching
                        this.templates[templateName]._isMultiSelect = true;

                        // Clear layers array - will be populated by instances
                        layer.layers = [];
                        layer._cleared = true;
                    }
                }

                // Editor mode: restore if cache has data but layer.layers was wiped
                if (this._layerInstances[templateName]?.length > 0 && layer.layers.length === 0) {
                    const templateParams = this.templatesPreview[templateName].params;

                    for (const cached of this._layerInstances[templateName]) {
                        // preserve option and position params to avoid resetting position on restore
                        const propsRestore = {
                            option: cached.params.option,
                            optionColor: cached.params.optionColor,
                            x: cached.params.x,
                            y: cached.params.y,
                            scaleX: cached.params.scaleX,
                            scaleY: cached.params.scaleY
                        };
                        Object.assign(cached.params, templateParams);
                        Object.assign(cached.params, propsRestore);

                        layer.layers.push(cached);
                    }
                    restored = true;
                }

            }
        }

        // Re-establish instance._layer references (safety check)
        for (const instance of this._instances) {
            if (!instance._layer) {
                const templateName = instance._templateName;
                instance._layer = this._layerInstances[templateName]?.find(
                    l => l.params.option === instance.name
                );
            }
        }

        for (const instance of this.option.options || []) {
            if (instance._templateName && instance._isLinkedColorOption !== true) {
                const linkedOption = this._getLinkedColorOption(instance) || this._setupLinkedColorInstance(instance, { sort: false });
                if (linkedOption && instance._layer && instance._layer.params.optionColor !== linkedOption.name) {
                    instance._layer.params.optionColor = linkedOption.name;
                }
            }
        }

        // One-time backfill: Create layers for instances created before previewConfig was available
        // Only runs once per controller lifecycle (async config case with popup)
        if (!this._layersBackfilled && this._instances.length > 0) {
            for (const instance of this._instances) {
                if (!instance._layer && !this.isMultiSelectTemplate(instance._templateName)) {
                    const templateName = instance._templateName;

                    // Skip if template doesn't have a preview layer configured
                    if (!this.layers[templateName] || !this.templatesPreview[templateName]) continue;

                    // Create new layer for this instance
                    const count = instance.name.split('_').pop();
                    let layerCopy = Util.deepCopy(this.templatesPreview[templateName]);
                    layerCopy.name += '_' + count;
                    layerCopy.params.option = instance.name;
                    layerCopy.params.optionParent = this.option.name;

                    this.layers[templateName].layers.push(layerCopy);

                    // Add to cache
                    if (!this._layerInstances[templateName]) {
                        this._layerInstances[templateName] = [];
                    }
                    this._layerInstances[templateName].push(layerCopy);

                    // Set reference on instance
                    instance._layer = layerCopy;
                    const linkedOption = this._getLinkedColorOption(instance) || this._setupLinkedColorInstance(instance, { sort: false });
                    if (linkedOption && instance._layer.params.optionColor !== linkedOption.name) {
                        instance._layer.params.optionColor = linkedOption.name;
                    }
                    restored = true;
                }
            }
            this._layersBackfilled = true;
        }

        // Trigger preview re-render if we restored layers
        if (restored) {
            this.onChange(this.option);
        }
    }

    onAdd(button, triggerChange = true, focus = triggerChange) {
        // multi-select uses onMultiSelect() instead
        if (this.isMultiSelectTemplate(button.name)) {
            return;
        }

        if (!this.canAdd(button.name)) {
            return;
        }

        let optionCopy = Util.deepCopy(this.templates[button.name], ['_*']);
        optionCopy._templateName = button.name;  // Track template origin

        let count = this._countOptions(button.name) + 1;
        // suffix count number of template
        optionCopy.id += '_' + count;
        optionCopy.name += '_' + count;
        optionCopy.label += ' ' + count;

        // Add to option.options (which _instances points to after syncInstances)
        this.option.options.push(optionCopy);
        this._sortOptions();
        this._clearErrors();

        // Directly add preview layer for new instance
        if (this.layers?.[button.name]) {
            let layerCopy = Util.deepCopy(this.templatesPreview[button.name]); // layer already created empty object
            layerCopy.name += '_' + count;
            layerCopy.params.option = optionCopy.name;
            layerCopy.params.optionParent = this.option.name;
            this.layers[button.name].layers.push(layerCopy);

            // Store in _layerInstances cache
            if (!this._layerInstances[button.name]) {
                this._layerInstances[button.name] = [];
            }
            this._layerInstances[button.name].push(layerCopy);

            // Set reference on option
            optionCopy._layer = layerCopy;
        }

        this._setupLinkedColorInstance(optionCopy, {
            openEditor: triggerChange,
            sort: triggerChange
        });

        // trigger change on new option (allows auto selection)
        if (triggerChange) {
            this.onChange(optionCopy);
        }

        // focus new option (scroll into view + type-specific focus)
        if (focus) {
            this.focusOption(optionCopy);
        }

        return optionCopy;
    }

    onRemove(option) {
        const templateName = option._templateName;
        if (!this.canRemove(templateName)) {
            return;
        }

        // If hasCustomUI instance, sync removal to template controller
        if (option._isMultiSelectInstance && this.hasCustomUI(templateName)) {
            const templateController = this.templates[templateName]._controller;
            // Upload-lift specific: remove URL from controller.urls
            if (templateController?.urls) {
                templateController._removeByUrl(option.value);
            }
        }

        let index = this.option.options.indexOf(option);
        if(index !== -1){
            this.option.options.splice(index, 1);
            this._removeLinkedColorOption(option);

            // if preview > remove layer connected to the removed option
            if(option._layer){
                let previewLayer = this.layers[templateName];
                let previewIndex = previewLayer.layers.indexOf(option._layer);
                if(previewIndex !== -1){
                    previewLayer.layers.splice(previewIndex, 1);
                }

                // Remove from _layerInstances cache
                const cacheIndex = this._layerInstances[templateName]?.indexOf(option._layer);
                if (cacheIndex !== -1) {
                    this._layerInstances[templateName].splice(cacheIndex, 1);
                }
            }

            // unless last element removed > we have to rebuild the counter for the same template
            if(index < this.option.options.length){
                this._rebuildCounter(templateName);
            }
        }
        this.onChange(this.option);
    }

    onDuplicate(option) {
        const templateName = option._templateName;
        if (!this.canAdd(templateName)) {
            return;
        }

        // deep copy with excluded properties
        let optionCopy = Util.deepCopy(option, ['_*']);
        optionCopy._templateName = templateName;  // Preserve template origin

        // Preserve multi-select flags
        if (option._isMultiSelectInstance) {
            optionCopy._isMultiSelectInstance = true;
            optionCopy._optionValue = option._optionValue;
            this._inheritController(optionCopy);
        }

        let count = this._countOptions(templateName) + 1;
        // suffix count number of template
        this._replaceIdName(optionCopy, count);

        this.option.options.push(optionCopy);
        this._sortOptions();

        // preview > add new layer connected to the new option
        if(this.layers?.[templateName]){
            const layer = option._layer;

            let layerCopy = Util.deepCopy(layer, ['_*']);

            // re-apply editor changes
            if(layer._editor){
                // INFO: we move the editor directly into the params (then renderer will create a new ._editor object)
                Object.assign(layerCopy.params, layer._editor);
                //layerCopy._editor = {};  // deep copy updated editor properties
                //Object.assign(layerCopy._editor, layer._editor);
            }

            layerCopy.name = layerCopy.name.substring(0, layerCopy.name.lastIndexOf('_')) + '_' + count;
            // connect to option
            layerCopy.params.option = optionCopy.name;
            layerCopy.params.optionParent = this.option.name;

            // move layer position below the original
            if(layer._object){
                // upload overlay and move a bit down
                if(layer.type === 'upload' || layer.type === 'overlay'){
                    layerCopy.params.x += 50;
                    layerCopy.params.y += 50;

                } else {
                    // default move below original
                    layerCopy.params.y += layer._object.height;
                }

                // copy flags to avoid autoPos on duplicate
                layerCopy._interactive = layer._interactive;
                layerCopy._interactiveScaled = layer._interactiveScaled;
                layerCopy._duplicate = true; // avoid reset of flags (upload-renderer)
            }

            // add to child layers
            this.layers[templateName].layers.push(layerCopy);

            // Store in _layerInstances cache
            if (!this._layerInstances[templateName]) {
                this._layerInstances[templateName] = [];
            }
            this._layerInstances[templateName].push(layerCopy);

            // Set reference on option
            optionCopy._layer = layerCopy;
        }

        const linkedColorOption = this._getLinkedColorOption(option);
        this._setupLinkedColorInstance(optionCopy, {
            value: linkedColorOption?.value,
            openEditor: true
        });

        // trigger change on new option (allows auto selection)
        this.onChange(optionCopy);
    }

    _countOptions(templateName) {
        let count = 0;
        for (let i = 0; i < this.option.options.length; i++) {
            if(this.option.options[i]._templateName === templateName){
                count++;
            }
        }
        return count;
    }

    _getInstanceByName(name) {
        return this.option.options?.find(option => option._templateName && option.name === name) || null;
    }

    _getLinkedColorOptionByParentName(instanceName) {
        return this.option.options?.find(option =>
            option._isLinkedColorOption === true && option._parentInstanceName === instanceName
        ) || null;
    }

    _getLinkedColorOption(instance) {
        if (!instance) return null;
        return this._getLinkedColorOptionByParentName(instance.name);
    }

    _getTemplateLinkedColorSourceName(templateName) {
        const templateLayerSource = this.templatesPreview[templateName]?.params?.optionColor;
        if (templateLayerSource) {
            return templateLayerSource;
        }

        const overlaySource = this._multiSelectTemplates[templateName]?._layers?.find(layer => layer.params?.optionColor);
        return overlaySource?.params?.optionColor || null;
    }

    _getLinkedColorSource(templateOrInstance) {
        const templateName = typeof templateOrInstance === 'string'
            ? templateOrInstance
            : templateOrInstance?._templateName;
        if (!templateName || !this.getOption) {
            return null;
        }

        const sourceName = this._getTemplateLinkedColorSourceName(templateName);
        if (!sourceName) {
            return null;
        }

        const sourceOption = this.getOption(sourceName);
        if (!this._supportsLinkedColorSource(sourceOption)) {
            return null;
        }

        return {
            name: sourceName,
            option: sourceOption
        };
    }

    _supportsLinkedColorSource(sourceOption) {
        if (!sourceOption || sourceOption.params?.multiSelect) {
            return false;
        }

        if (sourceOption.type === 'color') {
            return true;
        }

        return typeof sourceOption.type === 'string' && sourceOption.type.includes('swatch');
    }

    hasLinkedColor(templateOrInstance) {
        return !!this._getLinkedColorSource(templateOrInstance);
    }

    getLinkedColorSourceNames() {
        return [...new Set(this.names
            .map(templateName => this._getLinkedColorSource(templateName)?.name)
            .filter(Boolean))];
    }

    canDuplicate(instance) {
        if (!instance?._templateName) return false;
        if (instance._isMultiSelectInstance && this.hasCustomUI(instance._templateName)) {
            return false;
        }
        return this.canAdd(instance._templateName);
    }

    _setLinkedColorEditorOpen(instance, open = true) {
        if (!instance) return;

        if (open) {
            const linkedOption = this._setupLinkedColorInstance(instance, { sort: false });
            if (linkedOption) {
                this._openLinkedColorInstanceName = instance.name;
            }
        } else if (this._openLinkedColorInstanceName === instance.name) {
            this._openLinkedColorInstanceName = null;
        }
    }

    toggleLinkedColorEditor(instance) {
        if (!instance) return;
        this._setLinkedColorEditorOpen(instance, this._openLinkedColorInstanceName !== instance.name);
        this.onChange(this.option);
    }

    _setupLinkedColorInstance(instance, { value, openEditor = false, sort = true } = {}) {
        if (!instance?._templateName) {
            return null;
        }

        if (!this.hasLinkedColor(instance)) {
            if (this._openLinkedColorInstanceName === instance.name) {
                this._openLinkedColorInstanceName = null;
            }
            return null;
        }

        if (value === undefined) {
            const restoreLinkedColor = this.templates[instance._templateName]?._linkedColorRestore;
            if (restoreLinkedColor && Object.prototype.hasOwnProperty.call(restoreLinkedColor, instance.name)) {
                value = restoreLinkedColor[instance.name];
                delete restoreLinkedColor[instance.name];
                if (!Object.keys(restoreLinkedColor).length) {
                    delete this.templates[instance._templateName]._linkedColorRestore;
                }
            }
        }

        const hadLinkedOption = !!this._getLinkedColorOption(instance);
        const linkedOption = this._ensureLinkedColorOption(instance, value);
        if (!linkedOption) {
            return null;
        }

        if (!hadLinkedOption && sort) {
            this._sortOptions();
        }

        if (instance._layer && instance._layer.params.optionColor !== linkedOption.name) {
            instance._layer.params.optionColor = linkedOption.name;
        }

        if (openEditor) {
            this._openLinkedColorInstanceName = instance.name;
        }

        return linkedOption;
    }

    _ensureLinkedColorOption(instance, explicitValue) {
        if (!instance?._templateName) {
            return null;
        }

        const source = this._getLinkedColorSource(instance);
        if (!source) {
            return null;
        }

        const sourceOption = source.option;
        let linkedOption = this._getLinkedColorOption(instance);
        const sourceClone = Util.deepCopy(sourceOption, ['_*']);
        const currentValue = linkedOption?.value;

        if (!linkedOption) {
            linkedOption = {};
            this.option.options.push(linkedOption);
        }

        Object.assign(linkedOption, sourceClone);

        linkedOption.id = `${instance.id}${LINKED_COLOR_SUFFIX}`;
        linkedOption.name = `${instance.name}${LINKED_COLOR_SUFFIX}`;
        linkedOption.label = sourceClone.label || linkedOption.label || 'Color';
        linkedOption.value = explicitValue !== undefined
            ? explicitValue
            : (currentValue !== undefined ? currentValue : sourceClone.value);
        linkedOption.required = false;
        linkedOption.visible = true;
        linkedOption.errors = linkedOption.errors || [];
        linkedOption.conditions = [];
        linkedOption._isLinkedColorOption = true;
        linkedOption._parentInstanceName = instance.name;
        linkedOption._parentTemplateName = instance._templateName;
        linkedOption._sourceOptionName = sourceOption.name;

        linkedOption.params = Object.assign({}, linkedOption.params);
        delete linkedOption.params.name;
        delete linkedOption.params.exclude;

        return linkedOption;
    }

    syncLinkedColors() {
        let needsSort = false;
        const activeSourceNames = this.getLinkedColorSourceNames();

        for (const sourceName of activeSourceNames) {
            const sourceOption = this.getOption?.(sourceName);
            if (!sourceOption) {
                continue;
            }

            if (!Object.prototype.hasOwnProperty.call(sourceOption, '_groupEditorLinkedColorConditions')) {
                sourceOption._groupEditorLinkedColorConditions = Util.deepCopy(sourceOption.conditions || []);
            }

            if (!isLinkedColorHidden(sourceOption.conditions)) {
                sourceOption.conditions = [Util.deepCopy(LINKED_COLOR_HIDDEN_CONDITION)];
            }

            if (sourceOption.visible !== false) {
                sourceOption.visible = false;
            }
        }

        const activeInstances = this.option.options?.filter(option => option._templateName) || [];
        const activeInstanceNames = new Set(activeInstances.map(instance => instance.name));

        for (const instance of activeInstances) {
            if (!this.hasLinkedColor(instance)) {
                const linkedOption = this._getLinkedColorOption(instance);
                if (linkedOption) {
                    if (instance._layer?.params?.optionColor === linkedOption.name) {
                        delete instance._layer.params.optionColor;
                    }
                    const linkedIndex = this.option.options.indexOf(linkedOption);
                    if (linkedIndex !== -1) {
                        this.option.options.splice(linkedIndex, 1);
                        needsSort = true;
                    }
                }
                if (this._openLinkedColorInstanceName === instance.name) {
                    this._openLinkedColorInstanceName = null;
                }
            }
        }

        for (const option of [...this.option.options]) {
            if (!option._isLinkedColorOption) continue;
            if (!activeInstanceNames.has(option._parentInstanceName)) {
                const linkedIndex = this.option.options.indexOf(option);
                if (linkedIndex !== -1) {
                    this.option.options.splice(linkedIndex, 1);
                    needsSort = true;
                }
            }
        }

        if (needsSort) {
            this._sortOptions();
        }
    }

    _removeLinkedColorOption(instance) {
        if (!instance) return;

        const linkedOption = this._getLinkedColorOption(instance);
        if (linkedOption) {
            const linkedIndex = this.option.options.indexOf(linkedOption);
            if (linkedIndex !== -1) {
                this.option.options.splice(linkedIndex, 1);
            }
        }

        if (this._openLinkedColorInstanceName === instance.name) {
            this._openLinkedColorInstanceName = null;
        }
    }

    _renameLinkedColorOption(instance, previousName) {
        const linkedOption = this._getLinkedColorOptionByParentName(previousName);
        if (!linkedOption) {
            return;
        }

        linkedOption._parentInstanceName = instance.name;
        linkedOption._parentTemplateName = instance._templateName;
        linkedOption.id = `${instance.id}${LINKED_COLOR_SUFFIX}`;
        linkedOption.name = `${instance.name}${LINKED_COLOR_SUFFIX}`;

        if (instance._layer) {
            instance._layer.params.optionColor = linkedOption.name;
        }

        if (this._openLinkedColorInstanceName === previousName) {
            this._openLinkedColorInstanceName = instance.name;
        }
    }

    _getGroupEditor(templateName) {
        return this.templates[templateName]?.params?.groupEditor || {};
    }

    _getLinkedValue(templateName) {
        const config = this._getGroupEditor(templateName);
        if (config.option && this.getOption) {
            const linkedOption = this.getOption(config.option);
            if (linkedOption && linkedOption.value !== undefined) {
                const val = parseInt(linkedOption.value, 10);
                if (!isNaN(val) && val >= 0) return val;
            }
        }
        return null; // no linked option or invalid value
    }

    _getMin(templateName) {
        const linkedValue = this._getLinkedValue(templateName);
        if (linkedValue !== null) return linkedValue; // linked option = exact count

        const config = this._getGroupEditor(templateName);
        return config.min ?? 1;
    }

    _getMax(templateName) {
        const linkedValue = this._getLinkedValue(templateName);
        if (linkedValue !== null) return linkedValue; // linked option = exact count

        const config = this._getGroupEditor(templateName);
        return config.max ?? 0; // 0 = unlimited
    }

    _hasLinkedOption(templateName) {
        return this._getLinkedValue(templateName) !== null;
    }

    canAdd(templateName) {
        const max = this._getMax(templateName);
        if (max === 0) return true;
        return this._countOptions(templateName) < max;
    }

    canRemove(templateName) {
        const min = this._getMin(templateName);
        // Multi-select: always allow removal (validated on submit)
        if (this.isMultiSelectTemplate(templateName)) return true;
        return this._countOptions(templateName) > min;
    }

    onValidate(option) {
        for (const templateName of this.names) {
            const min = this._getMin(templateName);
            const count = this._countOptions(templateName);
            if (min > 0 && count < min) {
                const templateLabel = this.templates[templateName]?.label || templateName;
                option.errors.push(
                    templateLabel + ': ' + Translate.get('product.options.validity.selectMin', { min })
                );
                return false;
            }
        }
        return true;
    }

    _clearErrors() {
        if (this.option.errors?.length) {
            this.option.errors = [];
        }
    }

    /**
     * Sync instances to match min/linked value (called on each render)
     * - Always adds instances to reach min
     * - Only auto-removes when linked option controls exact count
     */
    syncMinInstances() {
        let changed = false;

        for (const button of this.buttons) {

            if (this.isMultiSelectTemplate(button.name)) {
                continue; // multi-select uses separate selection logic
            }

            const min = this._getMin(button.name);
            const currentCount = this._countOptions(button.name);

            if (currentCount < min) {
                // Add instances to reach min
                const needed = min - currentCount;
                for (let i = 0; i < needed; i++) {
                    this.onAdd(button, false);
                }
                changed = true;
            } else if (this._hasLinkedOption(button.name) && currentCount > min) {
                // Only auto-remove when linked option controls exact count
                const excess = currentCount - min;
                const instances = this.option.options.filter(o => o._templateName === button.name);
                for (let i = 0; i < excess; i++) {
                    const toRemove = instances[instances.length - 1 - i];
                    this._removeInstance(toRemove);
                }
                changed = true;
            }
        }

        if (changed) {
            this._sortOptions();
            // IMPORTANT: use setTimeout to avoid interfering with ongoing render cycle (breaks other components like Upload-Lift)
            setTimeout(() => this.onChange(this.option));
        }
    }

    // Internal remove method - bypasses min check (used by syncMinInstances)
    _removeInstance(option) {
        const index = this.option.options.indexOf(option);
        if (index === -1) return;

        this.option.options.splice(index, 1);
        this._removeLinkedColorOption(option);
        const templateName = option._templateName;

        // Remove layer if exists
        if (option._layer) {
            const previewLayer = this.layers[templateName];
            const previewIndex = previewLayer?.layers?.indexOf(option._layer);
            if (previewIndex !== -1) {
                previewLayer.layers.splice(previewIndex, 1);
            }
            const cacheIndex = this._layerInstances[templateName]?.indexOf(option._layer);
            if (cacheIndex !== -1) {
                this._layerInstances[templateName].splice(cacheIndex, 1);
            }
        }

        // Rebuild counter if not last
        if (index < this.option.options.length) {
            this._rebuildCounter(templateName);
        }
    }

    _replaceIdName(option, count) {
        option.id = option.id.substring(0, option.id.lastIndexOf('_')) + '_' + count;
        option.name = option.name.substring(0, option.name.lastIndexOf('_')) + '_' + count;
        option.label = option.label.substring(0, option.label.lastIndexOf(' ')) + ' ' + count;
    }

    _rebuildCounter(templateName) {
        let count = 0;
        // rebuild counter for given template (allows to only have single count variable)
        for (const option of this.option.options) {
            if(option._templateName === templateName){
                count++;
                const previousName = option.name;

                this._replaceIdName(option, count);
                this._renameLinkedColorOption(option, previousName);

                // update layer name option reference
                if(option._layer){
                    if(option._layer.name && option._layer.name.lastIndexOf('_') !== -1){
                        option._layer.name = option._layer.name.substring(0, option._layer.name.lastIndexOf('_')) + '_' + count;
                    }
                    option._layer.params.option = option.name;
                }
            }
        }
    }

    _sortOptions() {
        // Get template name order from buttons (matches template order)
        const templateOrder = this.buttons.map(b => b.name);

        this.option.options.sort((a, b) => {
            const getTemplateIndex = (option) => {
                const templateName = option._templateName || option._parentTemplateName;
                const index = templateOrder.indexOf(templateName);
                return index === -1 ? Number.MAX_SAFE_INTEGER : index;
            };
            const getInstanceIndex = (option) => {
                const instanceName = option._templateName ? option.name : option._parentInstanceName;
                const match = instanceName?.match(/_(\d+)$/);
                return match ? parseInt(match[1], 10) : 0;
            };
            const getRank = (option) => option._isLinkedColorOption ? 1 : 0;

            return getTemplateIndex(a) - getTemplateIndex(b) ||
                getInstanceIndex(a) - getInstanceIndex(b) ||
                getRank(a) - getRank(b) ||
                a.name.localeCompare(b.name);
        });
    }

    /**
     * Variant placement only follows the primary when both share the same ref geometry
     * (e.g. grayscale variant of the same print area - x/y are absolute canvas coordinates,
     * so they only transfer between identical geometries). Variants with different geometry
     * (e.g. one layer per phone brand with own refRect) keep their own placement.
     */
    _hasSameRefGeometry(primaryParams, variantParams) {
        if (!primaryParams.refRect && !variantParams.refRect) return true; // both full canvas
        return !!primaryParams.refRect === !!variantParams.refRect &&
            primaryParams.refX === variantParams.refX &&
            primaryParams.refY === variantParams.refY &&
            primaryParams.refWidth === variantParams.refWidth &&
            primaryParams.refHeight === variantParams.refHeight &&
            (primaryParams.refAngle || 0) === (variantParams.refAngle || 0);
    }

    /**
     * Sync variant parent children from primary parent.
     * Variant parents share the same params.option but have different conditions/filters
     * (e.g. normal upload vs grayscale upload). Children are created with variant-specific
     * template properties (like filters/refRect) and identity (name/option) synced from primary.
     * Placement (x/y/scale + interactive flags) is only synced between variants with the same
     * ref geometry - otherwise each variant keeps its own placement relative to its own refRect
     * (remembered placement per variant).
     */
    _syncVariantChildren() {
        if (!this._hasVariants) return;
        for (const templateName of this.names) {
            const variants = this._variantParents[templateName];
            if (!variants?.length) continue;

            const primaryChildren = this.layers[templateName]?.layers || [];
            const primaryTemplateParams = this.templatesPreview[templateName]?.params || {};

            for (const { parent, template } of variants) {
                const syncPlacement = this._hasSameRefGeometry(primaryTemplateParams, template.params);
                const existingChildren = parent.layers || [];
                const synced = [];

                for (const primaryChild of primaryChildren) {
                    // Reuse existing variant child (preserves renderer state like _object)
                    let varChild = existingChildren.find(
                        vc => vc.params.option === primaryChild.params.option
                    );

                    if (!varChild) {
                        // Create new variant child from variant template
                        varChild = Util.deepCopy(template);
                    }

                    // Sync identity properties from primary
                    varChild.name = primaryChild.name;
                    varChild.params.option = primaryChild.params.option;
                    varChild.params.optionParent = primaryChild.params.optionParent;
                    varChild.params.optionColor = primaryChild.params.optionColor;

                    if (syncPlacement) {
                        // Same ref geometry: variant mirrors the primary placement
                        varChild.params.x = primaryChild.params.x;
                        varChild.params.y = primaryChild.params.y;
                        varChild.params.scaleX = primaryChild.params.scaleX;
                        varChild.params.scaleY = primaryChild.params.scaleY;

                        // Sync renderer state (flags prevent autoPos/upload reset)
                        varChild._interactive = primaryChild._interactive;
                        varChild._interactiveScaled = primaryChild._interactiveScaled;
                    }

                    varChild._duplicate = true; // prevent reset
                    if (primaryChild._editor) {
                        if (!varChild._editor) varChild._editor = {};
                        if (syncPlacement) {
                            Object.assign(varChild._editor, primaryChild._editor);
                        } else {
                            // exclude placement keys (interactive moves are merged back into _editor in product-preview)
                            const { x, y, scaleX, scaleY, angle, ...editorStyle } = primaryChild._editor;
                            Object.assign(varChild._editor, editorStyle);
                        }
                    }

                    synced.push(varChild);
                }

                parent.layers = synced;
            }
        }
    }

    // ---- Cart restore methods ----

    /**
     * Restore instances from cart values
     * Called when cart properties are available (after async cart fetch)
     * May need to clear existing instances created by syncMinInstances on initial render
     * @param {Object} values - The cart properties object (passed from setValues)
     */
    restore(values) {
        if (!values || this._restored) return false;

        this._openLinkedColorInstanceName = null;

        for (const templateName of this.names) {
            if (this.templates[templateName]) {
                delete this.templates[templateName]._linkedColorRestore;
            }
        }

        // Clear any existing instances (may have been created by syncMinInstances before cart fetch)
        if (this.option.options.length > 0) {
            // Clear instances and their layers
            for (const instance of [...this.option.options]) {
                if (instance._layer) {
                    const templateName = instance._templateName;
                    const previewLayer = this.layers?.[templateName];
                    if (previewLayer?.layers) {
                        const layerIndex = previewLayer.layers.indexOf(instance._layer);
                        if (layerIndex !== -1) {
                            previewLayer.layers.splice(layerIndex, 1);
                        }
                    }
                    if (this._layerInstances[templateName]) {
                        const cacheIndex = this._layerInstances[templateName].indexOf(instance._layer);
                        if (cacheIndex !== -1) {
                            this._layerInstances[templateName].splice(cacheIndex, 1);
                        }
                    }
                }
            }
            this.option.options.length = 0;
            // Reset counters for clean restore
            this._instances = [];
        }

        let restored = false;

        // For each template, find values with pattern "templateName_N"
        for (const templateName of this.names) {
            const instanceValues = this._findInstanceValues(values, templateName);
            if (instanceValues.length === 0) continue;
            const template = this.templates[templateName];

            const linkedColorRestore = {};
            for (let i = 0; i < instanceValues.length; i++) {
                const linkedColorValue = values[`${templateName}_${instanceValues[i].index}${LINKED_COLOR_SUFFIX}`];
                if (linkedColorValue !== undefined) {
                    linkedColorRestore[`${templateName}_${i + 1}`] = linkedColorValue;
                }
            }
            if (Object.keys(linkedColorRestore).length) {
                template._linkedColorRestore = linkedColorRestore;
            }

            // Multi-select templates (upload-lift, library, swatch) need special handling
            if (this.isMultiSelectTemplate(templateName)) {
                if (this.hasCustomUI(templateName)) {
                    // hasCustomUI (upload-lift): set combined value on template
                    // The template's own controller.update() will handle restore asynchronously
                    // syncMultiSelectFromValue will create instances when upload events fire
                    const combinedValue = instanceValues.map(iv => iv.value).join(',');
                    template.value = combinedValue;
                    restored = true;
                } else {
                    // Non-hasCustomUI multi-select (library, swatch): use onMultiSelect for each value
                    // This properly populates _optionValue for thumbnail display
                    for (const { value } of instanceValues) {
                        this.onMultiSelect(templateName, value, false); // false = don't trigger onChange (batched at end)
                        restored = true;
                    }
                }
                continue;
            }

            // Standard templates: create instance for each found value
            const button = this.buttons.find(b => b.name === templateName);
            if (!button) continue;

            for (const { value } of instanceValues) {
                const instance = this.onAdd(button, false, false); // no change trigger, no focus
                if (instance) {
                    instance.value = value;
                    const editorPatch = parseTextEditorProperty(values['_' + instance.name + '_editor']);
                    if (Object.keys(editorPatch).length) {
                        instance._restoreEditor = editorPatch;
                    }
                    restored = true;
                }
            }
        }

        if (restored) {
            this._restored = true;
            this._sortOptions();
            // Trigger single onChange to re-render with restored values
            this.onChange(this.option);
        }

        return restored;
    }

    /**
     * Find values matching pattern "templateName_N" from cart properties
     * @param {Object} values - Cart properties object
     * @param {string} templateName - Template name to match
     * @returns {Array} Array of {index, value} objects sorted by index
     */
    _findInstanceValues(values, templateName) {
        // Escape special regex characters in template name
        const escaped = templateName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const pattern = new RegExp(`^${escaped}_(\\d+)$`);
        const found = [];

        for (const key in values) {
            if (!values.hasOwnProperty(key)) continue;
            const match = key.match(pattern);
            if (match && values[key]) {
                found.push({
                    index: parseInt(match[1], 10),
                    value: values[key]
                });
            }
        }

        // Sort by index to maintain order
        found.sort((a, b) => a.index - b.index);
        return found;
    }

    // ---- Collapse state methods ----

    isOpen(templateName) {
        // If collapse is disabled for this group editor, always return open
        if (this.option.params?.groupCollapse === false) return true;
        return this._collapseState[templateName] === true;
    }

    setOpen(templateName, open) {
        this._collapseState[templateName] = open;
    }

    toggleOpen(templateName) {
        this._collapseState[templateName] = !this._collapseState[templateName];
    }

    // ---- Card metadata methods ----

    getInstancesOfTemplate(templateName) {
        return this.option.options?.filter(o => o._templateName === templateName) || [];
    }

    getCardIcon(type) {
        return TYPE_ICONS[type] || SVG_ICONS.DEFAULT;
    }

    // ---- multi-select methods ----

    isMultiSelectTemplate(templateName) {
        return !!this._multiSelectTemplates[templateName];
    }

    hasCustomUI(templateName) {
        return this._multiSelectTemplates[templateName]?.hasCustomUI ?? false;
    }

    /**
     * Handle multi-select value selection (creates instance from selected value)
     * @param {string} templateName - Template name
     * @param {string} selectedValue - The selected value
     * @param {boolean} triggerChange - Whether to trigger onChange (default true, false for batch restore)
     */
    onMultiSelect(templateName, selectedValue, triggerChange = true) {
        const msData = this._multiSelectTemplates[templateName];
        if (!msData) return;

        if (!this.canAdd(templateName)) return;

        // Find the option value definition (for thumbnail/label)
        let optionValue = msData.option?.values?.find(v => v.value === selectedValue);

        // library option edge case: use controller selected image (.value can contain just category or empty)
        if(!optionValue && msData.option._controller?.image){
            optionValue = msData.option._controller?.image;
        }

        // Cart restore case: value is URL but not found in values array
        // Create minimal optionValue for thumbnail display
        if (!optionValue && selectedValue && selectedValue.startsWith('http')) {
            optionValue = { img: selectedValue, value: selectedValue };
        }

        // Create option instance (same pattern as onAdd)
        let optionCopy = Util.deepCopy(this.templates[templateName], ['_*']);
        optionCopy._templateName = templateName;
        optionCopy._isMultiSelectInstance = true;  // Flag for UI differentiation
        optionCopy._optionValue = optionValue;      // Cache for display
        optionCopy.value = selectedValue;           // The selected value
        this._inheritController(optionCopy);

        // Standard naming pattern
        let count = this._countOptions(templateName) + 1;
        optionCopy.id += '_' + count;
        optionCopy.name += '_' + count;
        optionCopy.label += ' ' + count;

        // Add to option.options (same as standard)
        this.option.options.push(optionCopy);
        this._sortOptions();
        this._clearErrors();

        // Handle preview layer creation
        if (msData._layers) {
            // overlay_values mode: find pre-created layer with matching condition
            const matchingLayer = msData._layers.find(layer => {
                return layer.conditions?.some(c =>
                    c.field === templateName && c.value === selectedValue
                );
            });

            if (matchingLayer && msData.parentLayer) {
                let layerCopy = Util.deepCopy(matchingLayer);
                layerCopy.name = `${matchingLayer.name}_${count}`;
                layerCopy.params.option = optionCopy.name;
                layerCopy.params.optionParent = this.option.name;
                layerCopy.conditions = [];  // Clear conditions - now active

                msData.parentLayer.layers.push(layerCopy);

                if (!this._layerInstances[templateName]) {
                    this._layerInstances[templateName] = [];
                }
                this._layerInstances[templateName].push(layerCopy);

                optionCopy._layer = layerCopy;
            }
        } else if (this.layers?.[templateName]) {
            // Standard multi-select mode: create layer from template (like onAdd)
            let layerCopy = Util.deepCopy(this.templatesPreview[templateName]);
            layerCopy.name += '_' + count;
            layerCopy.params.option = optionCopy.name;
            layerCopy.params.optionParent = this.option.name;

            // IMPORTANT: overwrite value with the selected img if available
            if (!optionCopy.value.startsWith('http') && optionValue?.img) {
                optionCopy.value = optionValue.img;
            }

            this.layers[templateName].layers.push(layerCopy);

            if (!this._layerInstances[templateName]) {
                this._layerInstances[templateName] = [];
            }
            this._layerInstances[templateName].push(layerCopy);

            optionCopy._layer = layerCopy;
        }

        this._setupLinkedColorInstance(optionCopy, {
            openEditor: triggerChange,
            sort: triggerChange
        });

        // Trigger change to re-render UI and preview (unless batching for restore)
        if (triggerChange) {
            this.onChange(optionCopy);
        }
    }

    // ---- hasCustomUI multi-select sync ----

    syncMultiSelectFromValue(templateName, value) {
        const values = value ? value.split(',').filter(v => v.trim()) : [];
        const existingInstances = this.getInstancesOfTemplate(templateName);
        const existingValues = existingInstances.map(i => i.value);

        // Add new values (create instance + layer for each)
        for (const val of values) {
            if (!existingValues.includes(val)) {
                this._addInstanceFromValue(templateName, val);
            }
        }

        // Remove missing values (remove instance + layer)
        for (const inst of existingInstances) {
            if (!values.includes(inst.value)) {
                this._removeInstanceByValue(inst);
            }
        }
    }

    // Internal: add without triggering onChange (batched at end)
    _addInstanceFromValue(templateName, val) {
        const msData = this._multiSelectTemplates[templateName];
        if (!msData) return;

        if (!this.canAdd(templateName)) return;

        let optionCopy = Util.deepCopy(this.templates[templateName], ['_*']);
        optionCopy._templateName = templateName;
        optionCopy._isMultiSelectInstance = true;
        optionCopy._optionValue = { img: val };  // Value is used as image URL
        optionCopy.value = val;
        this._inheritController(optionCopy);

        let count = this._countOptions(templateName) + 1;
        optionCopy.id += '_' + count;
        optionCopy.name += '_' + count;
        optionCopy.label += ' ' + count;

        this.option.options.push(optionCopy);
        this._sortOptions();

        // Create layer if template has preview
        if (this.layers?.[templateName]) {
            let layerCopy = Util.deepCopy(this.templatesPreview[templateName]);
            layerCopy.name += '_' + count;
            layerCopy.params.option = optionCopy.name;
            layerCopy.params.optionParent = this.option.name;
            this.layers[templateName].layers.push(layerCopy);

            if (!this._layerInstances[templateName]) {
                this._layerInstances[templateName] = [];
            }
            this._layerInstances[templateName].push(layerCopy);
            optionCopy._layer = layerCopy;
        }

        this._setupLinkedColorInstance(optionCopy);
    }

    // Internal: remove without triggering onChange (batched at end)
    _removeInstanceByValue(option) {
        const templateName = option._templateName;
        let index = this.option.options.indexOf(option);
        if (index !== -1) {
            this.option.options.splice(index, 1);
            this._removeLinkedColorOption(option);

            if (option._layer) {
                let previewLayer = this.layers[templateName];
                let previewIndex = previewLayer.layers.indexOf(option._layer);
                if (previewIndex !== -1) {
                    previewLayer.layers.splice(previewIndex, 1);
                }
                const cacheIndex = this._layerInstances[templateName]?.indexOf(option._layer);
                if (cacheIndex !== -1) {
                    this._layerInstances[templateName].splice(cacheIndex, 1);
                }
            }

            if (index < this.option.options.length) {
                this._rebuildCounter(templateName);
            }
        }
    }

    /**
     * Inherit _controller reference from template (stripped by deepCopy ['_*'])
     * Ensures multi-select instances share the same controller as their template (e.g. LibraryController)
     */
    _inheritController(instance) {
        const controller = this.templates[instance._templateName]?._controller;
        if (controller) {
            instance._controller = controller;
        }
    }

    // ---- Empty option detection ----

    _isOptionEmpty(option) {
        // Library type: check controller's image object  TODO library and swatch custom logic to only show once with multi select.
        if (option.type === 'library' && option._controller) {
            return !option._controller.image;
        }

        // Most types: check option.value (empty string, null, undefined)
        return option.value === undefined || option.value === null || option.value === '';
    }

    getFirstEmptyOption(templateName) {
        const instances = this.getInstancesOfTemplate(templateName);
        return instances.find(option => this._isOptionEmpty(option)) || null;
    }

    // ---- Focus methods ----

    _ensureVisible(optionEl) {
        if (!optionEl) return;

        // Find card panel parent
        const cardPanel = optionEl.closest('.cl-po--card-panel');
        if (!cardPanel) return;

        const collapseEl = cardPanel.previousElementSibling;
        if (!collapseEl || collapseEl.classList.contains('cl-po--open')) return;

        // Animate open: set max-height for transition
        cardPanel.style.maxHeight = cardPanel.scrollHeight + 100 + 'px';
        collapseEl.classList.add('cl-po--open');

        // Update controller state to keep in sync
        for (const button of this.buttons) {
            if (this.getInstancesOfTemplate(button.name).some(o => o.name === optionEl.dataset.option)) {
                this.setOpen(button.name, true);
                break;
            }
        }

        // Reset max-height after animation
        setTimeout(() => {
            cardPanel.style.maxHeight = null;
        }, 500);
    }

    focusOption(option) {
        if (!option) return;

        if (this.hasLinkedColor(option) && this._openLinkedColorInstanceName !== option.name) {
            this._setLinkedColorEditorOpen(option, true);
            this.onChange(this.option);
        }

        // Wait for DOM to update (lit-html is async)
        requestAnimationFrame(() => {
            const optionEl = document.querySelector(`[data-option="${option.name}"]`);
            if (!optionEl) return;

            // Open collapsed panel if needed
            this._ensureVisible(optionEl);

            // Scroll option into view
            optionEl.scrollIntoView({ behavior: 'smooth', block: 'center' });

            // Type-specific focus with delay for scroll to complete
            setTimeout(() => {
                this._focusByType(optionEl, option.type);
            }, 300);
        });
    }

    _focusByType(optionEl, type) {
        switch (type) {
            case 'upload':
            case 'upload-lift':
                // Trigger file picker by clicking the upload button
                const fileInputEl = optionEl.querySelector(`input[type=file]`);
                if (fileInputEl) fileInputEl.click();
                break;

            case 'library':
                // Library popup auto open
                const swatchBtn = optionEl.querySelector('[data-library-popup] .cl-po--swatch');
                if (swatchBtn) swatchBtn.click();
                break;

            default:
                // Default: focus first input/textarea/select
                const input = optionEl.querySelector('input, textarea, select');
                if (input) input.focus({ preventScroll: true });
                break;
        }
    }

}

export default GroupEditorController;

TemplateRegistry.templates['group-editor'] = (option, config, onChange, onErrorClear, getOption) => {
    // Simple init check
    const init = option._controller === undefined;

    // Get or create controller
    option._controller = option._controller || new GroupEditorController(onChange, getOption);
    const controller = option._controller;

    // Always update references (they change in editor mode)
    controller.option = option;
    controller.config = config;

    // One-time initialization (extract templates)
    if (init) {
        controller.initTemplates();
    }

    // Detect if previewConfig just became available (async config load with popup)
    const previewConfigNewlyAvailable = config.previewConfig && !controller._previewLayersSynced;

    // Editor mode OR init OR previewConfig newly available: sync instances and preview layers
    // This handles updateOptions(), updatePreview(), and async config load cases
    if (init || config.editorMode || previewConfigNewlyAvailable) {
        // 1. First sync instances (may re-extract templates if server resent them)
        controller.syncInstances();

        // 2. Then sync preview layers (needs templates + instances to be ready)
        if (config.previewConfig) {
            controller.syncPreviewLayers();
            controller._previewLayersSynced = true;
        }
    }

    // Cart restore: detect if we have values to restore
    // _restoreValues is set by setValues() after async cart fetch
    // At this point, syncMinInstances may have already created instances on initial render
    // EXCLUDE editor mode (should not restore in editor)
    if (!config.editorMode && option._restoreValues && !controller._restored) {
        controller.restore(option._restoreValues);
        delete option._restoreValues; // Clear after restore
    }

    // Sync instance count to min (or linked option value)
    // Runs on every render to handle dynamic linked option changes
    if (controller._initialized) {
        controller.syncMinInstances();
        controller.syncLinkedColors();
    }

    // Sync variant children on every render (handles position/scale changes from
    // renderer interactions and condition switches triggered by other options)
    if (controller._hasVariants) {
        controller._syncVariantChildren();
    }

    // Helper to toggle card panel animation
    const toggleCardPanel = (collapseEl, openCallback) => {
        if (!collapseEl) return;
        const panel = collapseEl.nextElementSibling;
        const isOpen = collapseEl.classList.contains('cl-po--open');

        if (!isOpen) {
            // Opening: set max-height to scrollHeight for animation
            panel.style.maxHeight = panel.scrollHeight + 100 + 'px';
            // After animation, reset to allow growing content
            setTimeout(() => {
                if (panel.style.maxHeight) {
                    panel.style.maxHeight = null;
                }
                if (openCallback) openCallback();
            }, 500);
        }
        // Toggle class
        collapseEl.classList.toggle('cl-po--open');
    };

    // Click handlers for split header zones
    const onAddZoneClick = (e, button) => {
        e.stopPropagation();

        // multi-select: clicking header just toggles open/closed (selection happens via swatch)
        if (controller.isMultiSelectTemplate(button.name)) {
            controller.toggleOpen(button.name);
            const collapseEl = e.currentTarget.closest('.cl-po--collapse'); // focus auto opens library or upload picker
            toggleCardPanel(collapseEl, () => controller.focusOption(controller.templates[button.name]));
            return;
        }

        // Check for existing empty option first
        const emptyOption = controller.getFirstEmptyOption(button.name);
        if (emptyOption) {
            // Open collapse if needed
            if (!controller.isOpen(button.name)) {
                controller.setOpen(button.name, true);
                const collapseEl = e.currentTarget.closest('.cl-po--collapse');
                if (collapseEl && !collapseEl.classList.contains('cl-po--open')) {
                    toggleCardPanel(collapseEl);
                }
            }
            // Focus existing empty option instead of adding
            controller.focusOption(emptyOption);
            return;
        }

        // No empty option found - add new one (if allowed)
        if (!controller.canAdd(button.name)) {
            return;
        }
        if (!controller.isOpen(button.name)) {
            controller.setOpen(button.name, true);
            const collapseEl = e.currentTarget.closest('.cl-po--collapse');
            if (collapseEl && !collapseEl.classList.contains('cl-po--open')) {
                toggleCardPanel(collapseEl);
            }
        }
        controller.onAdd(button);
    };

    const onToggleZoneClick = (e, templateName) => {
        e.stopPropagation();
        // Skip if collapse disabled
        if (option.params?.groupCollapse === false) return;
        controller.toggleOpen(templateName);
        // Trigger animation on the parent collapse element
        const collapseEl = e.currentTarget.closest('.cl-po--collapse');
        toggleCardPanel(collapseEl);
    };

    const linkedColorPanel = (instance, panelClass = '') => {
        const linkedOption = controller._getLinkedColorOption(instance);
        if (!linkedOption) {
            return null;
        }

        return html`
            <div class="cl-po--linked-color-panel ${controller._openLinkedColorInstanceName === instance.name ? 'cl-po--open' : ''} ${panelClass}">
                ${optionsTemplate([linkedOption], config, onChange, onErrorClear, getOption)}
            </div>
        `;
    };

    const instanceActions = (instance, controller) => html`
        <div class="cl-po--group-editor-actions">
            ${controller.hasLinkedColor(instance) ? html`
                <button type="button"
                        class="${controller._openLinkedColorInstanceName === instance.name ? 'cl-po--active' : ''}"
                        style=${styleMap({ width: '60px' })}
                        @click=${() => controller.toggleLinkedColorEditor(instance)}
                        data-tippy-content="${Translate.get('product.options.layer.color', null, 'Color')}"
                        data-tippy-placement="bottom">
                    ${unsafeHTML(SVG_ICONS.COLOR)}
                </button>
            ` : null}
            ${controller.canDuplicate(instance) ? html`
                <button type="button"
                        @click=${() => controller.onDuplicate(instance)}
                        data-tippy-content="${Translate.get('product.options.layer.duplicate', null, 'Duplicate')}"
                        data-tippy-placement="bottom">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18"><path d="M6.9998 6V3C6.9998 2.44772 7.44752 2 7.9998 2H19.9998C20.5521 2 20.9998 2.44772 20.9998 3V17C20.9998 17.5523 20.5521 18 19.9998 18H16.9998V20.9991C16.9998 21.5519 16.5499 22 15.993 22H4.00666C3.45059 22 3 21.5554 3 20.9991L3.0026 7.00087C3.0027 6.44811 3.45264 6 4.00942 6H6.9998ZM5.00242 8L5.00019 20H14.9998V8H5.00242ZM8.9998 6H16.9998V16H18.9998V4H8.9998V6Z" fill="currentColor"></path></svg>
                </button>
            ` : null}
            ${controller.canRemove(instance._templateName) ? html`
                <button type="button"
                        @click=${() => controller.onRemove(instance)}
                        data-tippy-content="${Translate.get('product.options.layer.delete', null, 'Delete')}"
                        data-tippy-placement="bottom">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18"><path d="M17 6H22V8H20V21C20 21.5523 19.5523 22 19 22H5C4.44772 22 4 21.5523 4 21V8H2V6H7V3C7 2.44772 7.44772 2 8 2H16C16.5523 2 17 2.44772 17 3V6ZM18 8H6V20H18V8ZM9 11H11V17H9V11ZM13 11H15V17H13V11ZM9 4V6H15V4H9Z" fill="currentColor"></path></svg>
                </button>
            ` : null}
        </div>
    `;

    const multiSelectSelectionRow = (inst) => html`
        <div class="cl-po--multi-select-instance" data-option="${inst.name}">
            <input type="hidden" name="${optionName(inst)}" id="${inst.id}" value=${inst.value} form=${ifDefined(config.productFormLink)} />
            <div class="cl-po--multi-select-selection">
                <div class="cl-po--selection-thumbnail">
                    ${inst._optionValue?.img
                        ? html`<img src="${cdnImg(inst._optionValue.img, cdnSize('small'))}" alt="${inst._optionValue.text || inst.value}" />`
                        : inst._optionValue?.color
                            ? html`<div class="cl-po--selection-color" style="background-color: ${inst._optionValue.color}"></div>`
                            : null
                    }
                </div>
                <div class="cl-po--selection-label">
                    ${inst._optionValue?.text || inst._optionValue?.value || inst.value}
                </div>
                ${instanceActions(inst, controller)}
            </div>
            ${linkedColorPanel(inst, 'cl-po--multi-select-linked-color')}
        </div>
    `;

    const multiSelectCardContent = (button, controller, config, onChange, onErrorClear, getOption) => {
        const template = controller.templates[button.name];
        const instances = controller.getInstancesOfTemplate(button.name);

        // Custom onChange wrapper - intercepts swatch selection to create instance
        const onMultiSelectChange = (changedOption) => {
            // Upload-lift multi-select with hasCustomUI handles its own instance creation
            if (!controller.hasCustomUI(button.name)) {
                if (changedOption.value) {
                    controller.onMultiSelect(button.name, changedOption.value);
                    // Clear the swatch selection to allow re-selection of same value
                    changedOption.value = '';
                }
            } else {
                // Template handles its own UI - sync instances with value
                controller.syncMultiSelectFromValue(button.name, changedOption.value);
                // Don't clear value - template needs it for form submission
            }
            onChange(changedOption);
        };

        return html`
            <div class="cl-po--card-body cl-po--multi-select-body">
                <div class="cl-po--multi-select-wrap">
                    ${optionsTemplate([template], config, onMultiSelectChange, onErrorClear, getOption)}
                </div>
                ${instances.length > 0 ? html`
                    <div class="cl-po--multi-select-selections">
                        ${repeat(instances, (inst) => inst.name, (inst) => multiSelectSelectionRow(inst))}
                    </div>
                ` : null}
            </div>
        `;
    };

    const editorTemplate = html`
        <div class="cl-po--group cl-po--group-editor">
            <div class="cl-po--group-editor-cards">
                ${repeat(controller.buttons, (button) => button.name, (button) => {
                    const instances = controller.getInstancesOfTemplate(button.name);
                    const iconSvg = controller.getCardIcon(button.type);
                    const isOpen = controller.isOpen(button.name);
                    const canAdd = controller.canAdd(button.name);

                    // Card body content - branch for multi-select vs standard templates
                    const isMultiSelect = controller.isMultiSelectTemplate(button.name);

                    const cardContent = isMultiSelect
                        ? multiSelectCardContent(button, controller, config, onChange, onErrorClear, getOption)
                        : html`
                            <div class="cl-po--card-body">
                                ${instances.length > 0 ? html`
                                    <div class="cl-po--card-instances">
                                        ${optionsTemplate(instances, config, onChange, onErrorClear, getOption, optionChild => html`
                                            ${instanceActions(optionChild, controller)}
                                            ${linkedColorPanel(optionChild)}
                                        `)}
                                    </div>
                                ` : null}
                                ${canAdd ? html`
                                    <button type="button" class="cl-po--card-add-btn" @click=${() => controller.onAdd(button)}>
                                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="16" height="16"><path fill="currentColor" d="M11 11V5h2v6h6v2h-6v6h-2v-6H5v-2z"/></svg>
                                        ${button.label}
                                    </button>
                                ` : null}
                            </div>
                        `;

                    // Card structure with split header zones (NOT using collapse() helper)
                    return html`
                        <div class="cl-po--card">
                            <div class="cl-po--collapse cl-po--card-collapse ${isOpen ? 'cl-po--open' : ''}">
                                ${option.params?.groupCollapse !== false ? html`
                                    <div class="cl-po--card-toggle-zone" @click=${(e) => onToggleZoneClick(e, button.name)}>
                                        <div class="cl-po--card-chevron">
                                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="20" height="20">
                                                <path fill="currentColor" d="M10.828 12l4.95 4.95-1.414 1.414L8 12l6.364-6.364 1.414 1.414z"/>
                                            </svg>
                                        </div>
                                    </div>
                                ` : null}
                                <div class="cl-po--card-add-zone" @click=${(e) => onAddZoneClick(e, button)}>
                                    <div class="cl-po--card-icon">${unsafeHTML(iconSvg)}</div>
                                    <div class="cl-po--card-text">
                                        <div class="cl-po--card-title">${button.label}</div>
                                    </div>
                                </div>
                            </div>
                            <div class="cl-po--card-panel">
                                ${cardContent}
                            </div>
                        </div>
                    `;
                })}
            </div>
        </div>`;

    // conditional collapse (wraps entire group editor if configured)
    return option.params.collapse ? collapse(unsafeHTML(option.params.collapseLabel), editorTemplate, option.params.collapseOpen) : editorTemplate;
}
